import { Link } from '@tanstack/react-router'
import { Column, ColumnDef } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, Ship } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Boat,
  BOAT_STATUS_LABELS,
  CURTAILMENT_FREQUENCY_SHORT,
  LISTING_STATE_LABELS,
} from '@/types/inventory'
import { assetUrl } from '@/lib/api'

const listingVariant: Record<Boat['listing_state'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  for_sale: 'default',
  sold: 'destructive',
  off_market: 'secondary',
}

// Click-to-sort header for a column: cycles asc -> desc -> unsorted, with the
// arrow showing the current direction.
function SortableHeader({
  column,
  children,
}: {
  column: Column<Boat, unknown>
  children: ReactNode
}) {
  const sorted = column.getIsSorted()
  return (
    <Button variant="ghost" onClick={() => column.toggleSorting(sorted === 'asc')}>
      {children}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-2 h-4 w-4" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-2 h-4 w-4" />
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  )
}

// Shared boat table columns. `showListing` hides the listing-state badge for
// views that only contain one state (e.g. the Sold tab); `showSold` adds the
// sold-on date column for the Sold tab.
export function boatColumns({ showListing = true, showSold = false }: { showListing?: boolean; showSold?: boolean } = {}): ColumnDef<Boat>[] {
  const listing: ColumnDef<Boat>[] = showListing
    ? [
        {
          accessorKey: 'listing_state',
          header: ({ column }) => <SortableHeader column={column}>Listing</SortableHeader>,
          cell: ({ row }) => {
            const state = row.original.listing_state
            return <Badge variant={listingVariant[state]}>{LISTING_STATE_LABELS[state]}</Badge>
          },
        },
      ]
    : []

  const sold: ColumnDef<Boat>[] = showSold
    ? [
        {
          accessorKey: 'sold_at',
          header: ({ column }) => <SortableHeader column={column}>Sold</SortableHeader>,
          cell: ({ row }) => {
            const soldAt = row.original.sold_at
            if (!soldAt) return <span className="text-muted-foreground">—</span>
            return (
              <span>
                {new Date(`${soldAt.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            )
          },
        },
      ]
    : []

  return [
    {
      id: 'cover',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        // ~480px ingest thumbnail; this cell renders at 40x28px.
        const url = row.original.cover_thumb_url ?? row.original.cover_image_url
        if (!url) {
          return (
            <div className="flex h-10 w-14 items-center justify-center rounded-md bg-muted">
              <Ship className="h-4 w-4 text-muted-foreground" />
            </div>
          )
        }
        return (
          <img
            src={assetUrl(url)}
            alt={`${row.original.make} ${row.original.model}`}
            className="h-10 w-14 rounded-md object-cover"
            loading="lazy"
          />
        )
      },
    },
    {
      // Sorts by "make model" so boats of the same make order by model.
      accessorFn: (boat) => `${boat.make} ${boat.model}`,
      id: 'make',
      header: ({ column }) => <SortableHeader column={column}>Make & Model</SortableHeader>,
      cell: ({ row }) => (
        <div className="font-medium">
          {row.original.make} {row.original.model}
          {row.original.consignment && (
            <Badge variant="outline" className="ml-2 border-info/30 bg-info/15 text-info">
              Consigned
            </Badge>
          )}
          {row.original.loan && (
            <Badge variant="outline" className="ml-2 border-warning/30 bg-warning/15 text-warning">
              Financed
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'year',
      header: ({ column }) => <SortableHeader column={column}>Year</SortableHeader>,
    },
    {
      accessorKey: 'condition',
      header: ({ column }) => <SortableHeader column={column}>Condition</SortableHeader>,
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {row.original.condition}
        </Badge>
      ),
    },
    {
      accessorKey: 'length_ft',
      header: ({ column }) => <SortableHeader column={column}>Length</SortableHeader>,
      cell: ({ row }) => <span>{row.original.length_ft} ft</span>,
    },
    {
      accessorKey: 'price',
      header: ({ column }) => <SortableHeader column={column}>Price</SortableHeader>,
      cell: ({ row }) => <span>${row.original.price.toLocaleString()}</span>,
    },
    {
      // Active floor-plan exposure: the recurring curtailment amount, with the
      // next due date when one is unpaid. Dash for unencumbered boats. Loans
      // with an APR also show the estimated first-period interest
      // (principal × APR ÷ periods/year) so the list hints at the full
      // carrying cost.
      id: 'curtailment',
      accessorFn: (boat) => boat.loan?.curtailment_amount ?? 0,
      header: ({ column }) => <SortableHeader column={column}>Curtailment</SortableHeader>,
      cell: ({ row }) => {
        const loan = row.original.loan
        if (!loan || loan.curtailment_amount <= 0) return <span className="text-muted-foreground">—</span>
        const unit = CURTAILMENT_FREQUENCY_SHORT[loan.frequency]
        const periodsPerYear = loan.frequency === 'quarterly' ? 4 : 12
        const interest =
          loan.interest_rate > 0
            ? (loan.principal * loan.interest_rate) / 100 / periodsPerYear
            : 0
        const due = loan.next_due_on
          ? new Date(`${loan.next_due_on.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })
          : null
        const overdue = loan.next_due_on && loan.next_due_on.slice(0, 10) < new Date().toISOString().slice(0, 10)
        return (
          <div className="text-sm">
            <div>
              ${loan.curtailment_amount.toLocaleString()}/{unit}
            </div>
            {interest > 0 && (
              <div className="text-muted-foreground">
                + ${Math.round(interest).toLocaleString()}/{unit} interest
              </div>
            )}
            {due && (
              <div className={overdue ? 'text-destructive' : 'text-muted-foreground'}>
                {overdue ? 'overdue ' : 'due '}
                {due}
              </div>
            )}
          </div>
        )
      },
    },
    ...listing,
    ...sold,
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column}>Operational</SortableHeader>,
      cell: ({ row }) => {
        const status = row.original.status
        const variant =
          status === 'available' ? 'outline' : status === 'in_service' ? 'secondary' : 'outline'
        return <Badge variant={variant}>{BOAT_STATUS_LABELS[status]}</Badge>
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <Link to="/inventory/$boatId" params={{ boatId: row.original.id }}>
          <Button variant="outline" size="sm">
            View
          </Button>
        </Link>
      ),
    },
  ]
}
